"use client";

import { useState } from "react";
import Icon from "./icon";

const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1080;
const PADDING = 60;
const LOGO_HEIGHT = 40;
const BG_COLOR = "#F5F0EB";
const TEXT_BG_COLOR = "#f0e7d6";

async function loadLogo(): Promise<HTMLImageElement> {
  const img = new Image();
  return new Promise((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = "/images/efn_logo_600x257.png";
  });
}

function drawLogo(
  ctx: CanvasRenderingContext2D,
  logoImg: HTMLImageElement,
  x: number,
  y: number,
  height: number,
) {
  const aspect = logoImg.width / logoImg.height;
  const width = height * aspect;
  ctx.drawImage(logoImg, x, y, width, height);
}

const AUTHOR_IMAGE_SIZE = 48;
const AUTHOR_SPACING = 24;

async function loadImage(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  return new Promise((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function drawCircleImage(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  size: number,
) {
  const strokeWidth = 3;
  // Draw stroke
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2 + strokeWidth, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fillStyle = "#e3d2b2";
  ctx.fill();
  ctx.restore();
  // Draw image clipped to circle
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(img, x, y, size, size);
  ctx.restore();
}

async function generateShareImage(
  imageUrl: string,
  title: string,
  articleUrl: string,
  author?: { name: string; imageUrl?: string; bylineImageUrl?: string },
): Promise<Blob> {
  // Ensure fonts are loaded for canvas use
  await document.fonts.ready;

  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d")!;

  // Determine if we should use byline layout (large author image on the right)
  const useBylineLayout = !!author?.bylineImageUrl;

  // Load article image and logo
  const proxyUrl = `/api/image-proxy?token=${encodeURIComponent(process.env.NEXT_PUBLIC_IMAGE_PROXY_TOKEN || "")}&url=${encodeURIComponent(imageUrl)}`;
  const [img, logoImg] = await Promise.all([loadImage(proxyUrl), loadLogo()]);

  // Load author image (small circle or large byline)
  let authorImg: HTMLImageElement | null = null;
  const authorImgUrl = useBylineLayout
    ? author!.bylineImageUrl!
    : author?.imageUrl;
  if (authorImgUrl) {
    try {
      const authorProxyUrl = `/api/image-proxy?token=${encodeURIComponent(process.env.NEXT_PUBLIC_IMAGE_PROXY_TOKEN || "")}&url=${encodeURIComponent(authorImgUrl)}`;
      authorImg = await loadImage(authorProxyUrl);
    } catch {
      // Author image failed to load, continue without it
    }
  }

  // Byline image dimensions for the right column
  const BYLINE_IMAGE_WIDTH = 300;

  // Measure how much space the title needs
  ctx.font = `bold 52px "Noto Serif", serif`;
  ctx.textBaseline = "top";
  const textAreaWidth = useBylineLayout
    ? CANVAS_WIDTH - BYLINE_IMAGE_WIDTH - PADDING - 20
    : CANVAS_WIDTH - PADDING * 2;
  const titleLines = getWrappedLines(ctx, title, textAreaWidth);
  const titleLineHeight = 64;
  const titleHeight = titleLines.length * titleLineHeight;

  // Layout constants
  const textBoxPaddingTop = PADDING;
  const textBoxPaddingBottom = PADDING;
  const urlSpacing = 20;
  const urlLineHeight = 24;
  const bottomRowSpacing = 20;

  // Calculate text box height to determine article image height
  let textBoxHeight: number;
  if (useBylineLayout) {
    const authorNameSpacing = 24;
    const authorNameHeight = 28;
    const logoSpacing = 16;
    textBoxHeight =
      textBoxPaddingTop +
      titleHeight +
      urlSpacing +
      urlLineHeight +
      authorNameSpacing +
      authorNameHeight +
      logoSpacing +
      LOGO_HEIGHT +
      textBoxPaddingBottom;
  } else {
    const bottomRowHeight = Math.max(LOGO_HEIGHT, AUTHOR_IMAGE_SIZE);
    textBoxHeight =
      textBoxPaddingTop +
      titleHeight +
      urlSpacing +
      urlLineHeight +
      bottomRowSpacing +
      bottomRowHeight +
      textBoxPaddingBottom;
  }

  // Article image fills remaining space
  const imageHeight = CANVAS_HEIGHT - textBoxHeight;

  // Draw background for entire canvas
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  // Draw text box background
  ctx.fillStyle = TEXT_BG_COLOR;
  ctx.fillRect(0, imageHeight, CANVAS_WIDTH, textBoxHeight);

  // Draw article image (top, full width)
  const imgAspect = img.width / img.height;
  const targetAspect = CANVAS_WIDTH / imageHeight;
  let sx = 0,
    sy = 0,
    sw = img.width,
    sh = img.height;
  if (imgAspect > targetAspect) {
    sw = img.height * targetAspect;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / targetAspect;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, CANVAS_WIDTH, imageHeight);

  // Draw title
  const titleY = imageHeight + textBoxPaddingTop;
  ctx.fillStyle = "#000000";
  ctx.font = `bold 52px "Noto Serif", serif`;
  ctx.textBaseline = "top";
  for (let i = 0; i < titleLines.length; i++) {
    ctx.fillText(titleLines[i], PADDING, titleY + i * titleLineHeight);
  }

  // Draw article URL
  const urlY = titleY + titleHeight + urlSpacing;
  ctx.fillStyle = "#666666";
  ctx.font = `16px "Noto Sans", sans-serif`;
  const displayUrl = articleUrl
    .replace(/^https?:\/\//, "")
    .replace(/\?.*$/, "");
  ctx.fillText(displayUrl, PADDING, urlY);

  if (useBylineLayout) {
    // === Byline layout: author name + logo stacked left, byline image bottom-right ===

    // Draw author name below URL
    const authorNameSpacing = 24;
    const authorNameHeight = 28;
    const nameY = urlY + urlLineHeight + authorNameSpacing;
    if (author) {
      ctx.fillStyle = "#000000";
      ctx.font = `500 22px "Noto Sans", sans-serif`;
      ctx.textBaseline = "top";
      ctx.fillText(author.name, PADDING, nameY);
    }

    // Draw EFN logo directly under author name
    const logoSpacing = 16;
    const logoY = nameY + authorNameHeight + logoSpacing;
    drawLogo(ctx, logoImg, PADDING, logoY, LOGO_HEIGHT);

    // Draw byline image (right side, bottom-right aligned)
    if (authorImg) {
      const bylineMaxHeight = textBoxHeight;
      const bylineAspect = authorImg.width / authorImg.height;
      const naturalHeight = BYLINE_IMAGE_WIDTH / bylineAspect;
      const drawH = Math.min(naturalHeight, bylineMaxHeight);
      const srcH = Math.round((drawH / naturalHeight) * authorImg.height);
      const bylineX = CANVAS_WIDTH - BYLINE_IMAGE_WIDTH;
      const bylineY = CANVAS_HEIGHT - drawH;
      ctx.drawImage(
        authorImg,
        0,
        0,
        authorImg.width,
        srcH,
        bylineX,
        bylineY,
        BYLINE_IMAGE_WIDTH,
        drawH,
      );
    }
  } else {
    // === Standard layout: small author circle + name bottom left, logo bottom right ===
    const bottomRowHeight = Math.max(LOGO_HEIGHT, AUTHOR_IMAGE_SIZE);
    const bottomRowY = canvas.height - textBoxPaddingBottom - bottomRowHeight;

    if (author) {
      let textX = PADDING;
      if (authorImg) {
        drawCircleImage(ctx, authorImg, PADDING, bottomRowY, AUTHOR_IMAGE_SIZE);
        textX = PADDING + AUTHOR_IMAGE_SIZE + 12;
      }
      ctx.fillStyle = "#000000";
      ctx.font = `500 20px "Noto Sans", sans-serif`;
      ctx.textBaseline = "middle";
      ctx.fillText(author.name, textX, bottomRowY + AUTHOR_IMAGE_SIZE / 2);
    }

    // Draw EFN logo (bottom right)
    const logoAspect = logoImg.width / logoImg.height;
    const logoWidth = LOGO_HEIGHT * logoAspect;
    const logoY = bottomRowY + (bottomRowHeight - LOGO_HEIGHT) / 2;
    drawLogo(
      ctx,
      logoImg,
      CANVAS_WIDTH - PADDING - logoWidth,
      logoY,
      LOGO_HEIGHT,
    );
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), "image/png");
  });
}

function getWrappedLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + " ";
    if (ctx.measureText(testLine).width > maxWidth && i > 0) {
      lines.push(line.trim());
      line = words[i] + " ";
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());
  return lines;
}

export default function ShareImageButton({
  title,
  imageUrl,
  articleUrl,
  slug,
  author,
}: {
  title: string;
  imageUrl: string;
  articleUrl: string;
  slug: string;
  author?: { name: string; imageUrl?: string; bylineImageUrl?: string };
}) {
  const [loading, setLoading] = useState(false);

  const handleShareImage = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const blob = await generateShareImage(
        imageUrl,
        title,
        articleUrl,
        author,
      );
      const file = new File([blob], `efn-${slug}.png`, { type: "image/png" });

      const isMobile = typeof window !== "undefined" && window.innerWidth < 700;

      if (navigator.share && isMobile) {
        await navigator.share({
          files: [file],
        });
      } else {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `efn-${slug}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.warn("Kunde inte skapa delningsbild:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      aria-label="Spara delningsbild"
      className="btn p-0 rounded-sm hover:bg-sand-800 bg-sand-700"
      onClick={handleShareImage}
      disabled={loading}
    >
      <span className="h-full bg-sand-800 py-2 px-2 rounded-sm md:rounded-none md:rounded-l-sm">
        <Icon.Image className="size-[18px] fill-black-500" />
      </span>
      <span className="hidden md:block px-2">
        {loading ? "Skapar bild..." : "Spara bild"}
      </span>
    </button>
  );
}
